import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Data
import { functionsList, hierarchiesList } from "constants/constants";

// Hooks
import { useAuth } from "utils/hooks";
import { useAppDispatch } from "app/hooks";
import useRedirectOnNoAdminPermission from "utils/hooks/useRedirectOnNoAdminPermission";

// Converters
import { convertPaceBehaviouralComparison } from "./adapters/converter.chart";

// Types
import {
    TPeopleData,
    TTeamsData,
    TFunction,
    THierarchy,
    IMultiFilters,
    ISelectedMultiFilters,
    TTeam,
    TPerson,
} from "./BehaviouralComparison.types";
import { LD12Chart } from "store/slice/charts";
import {
    BCIndividualsRefResponse,
    BCTeamsRefResponse,
    BehaviouralComparisonResponse,
    getBehaviouralComparisonData,
    getPaceDashboardIndividualsRef,
    getPaceDashboardTeamsRef,
} from "services/pace/dashboard.service";
import { toggleLoader } from "store/slice/UI";
import { isEmpty } from "lodash";
import { convertTeamsRef } from "./adapters/converter.teamsRef";
import { convertIndividualsRef } from "./adapters/converter.individualsRef";
import { urlPaths } from "enums/urlPaths";

export type TList = "individuals" | "teams";

export const mockedSavedFilters = [
    { id: 0, name: "Finance Set" },
    { id: 1, name: "CFO Set" },
    { id: 2, name: "ProductTeam" },
];

const defaultMetaParams = {
    page_size: 10,
    page_no: 1,
};

const chartRequestDefaultParams = {
    individuals: { user_ids: [] },
    averages: [],
};

const useBehaviouralComparison = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { loggedInUserData } = useAuth();
    const orgId = loggedInUserData.selectedOrg?.id;

    useRedirectOnNoAdminPermission("SAAS_PACE_DASHBOARD_BEHAVIOURAL_COMPARISON", urlPaths.NotFound);

    const [chartData, setChartData] = useState<LD12Chart>({} as LD12Chart);
    const [showAverage, setShowAverage] = useState<boolean>(false);
    const [openTeamsModal, setOpenTeamsModal] = useState<boolean>(false);
    const [openBenchmarkPopover, setOpenBenchmarkPopover] = useState<boolean>(false);

    // Saved Filters
    const savedFilters = mockedSavedFilters;

    // Filters
    const [people, setPeople] = useState<TPeopleData>({ meta: defaultMetaParams, status: "pristine" } as TPeopleData);
    const [teams, setTeams] = useState<TTeamsData>({ meta: defaultMetaParams, status: "pristine" } as TTeamsData);
    const [functions, setFunctions] = useState<TFunction[]>(functionsList);
    const [hierarchies, setHierarchies] = useState<THierarchy[]>(hierarchiesList);

    const [multiFilterData, setMultiFilterData] = useState<IMultiFilters>({
        teams: [],
        functions: functionsList,
        hierarchies: hierarchiesList,
    });

    // Multi Select Field
    const [selectedMultiFilter, setSlelectedMultiFilter] = useState<ISelectedMultiFilters>({
        selectedTeams: [],
        selectedFunctions: [],
        selectedHierarchies: [],
    });

    const handleUpdateChartData = (data: LD12Chart, shouldShowAverage: boolean) => {
        setChartData({
            ...data,
            series_labels: data?.series_labels.map((serie_label) => ({
                ...serie_label,
                enabled: serie_label.label === "Hub Average" ? shouldShowAverage : true,
            })),
        });
    };

    const handleToggleAverage = (event: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.target.checked;
        setShowAverage(event.target.checked);
        handleUpdateChartData(chartData, isChecked);
    };

    const handleOpenBenchmarkPopover = () => {
        setOpenBenchmarkPopover(!openBenchmarkPopover);
    };

    const handleOnSave = () => {
        console.log("save clicked!");
    };

    const handleOnClear = () => {
        console.log("clear clicked!");
    };

    const handleOpenTeamsModal = (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        ev.preventDefault();
        setOpenTeamsModal(true);
    };

    const handleCloseTeamsModal = async () => {
        setOpenTeamsModal(false);

        // When closing the modal request again the teams to make sure we have the lastest
        if (orgId) {
            const { data } = await getPaceDashboardTeamsRef({ ...defaultMetaParams, page_size: 50, orgId });
            handleSetResults<BCTeamsRefResponse>(data, "teams");
        }
    };

    const handleFetchChart = async <T extends TPeopleData["selected"] | TTeamsData["selected"]>(
        selectedData: T,
        type: TList,
        avoidFetching = false
    ) => {
        if (!orgId || avoidFetching) return;

        let params = {};

        if (type === "individuals") {
            params = {
                individuals: {
                    id_suffix: "individuals",
                    user_ids: (selectedData as TPerson[]).filter((p) => p.checked).map((p) => p.id),
                },
                averages:
                    teams.selected
                        ?.filter((t) => t.checked)
                        .map((t) => ({
                            id_suffix: t.name,
                            teams: [t.name],
                        })) || [],
            };
        }

        if (type === "teams") {
            params = {
                individuals: {
                    id_suffix: "individuals",
                    user_ids: people.selected?.filter((p) => p.checked).map((p) => p.id) || [],
                },
                averages: (selectedData as TTeam[])
                    .filter((t) => t.checked)
                    .map((t) => ({
                        id_suffix: t.name.replaceAll(" ", "_"),
                        teams: [t.name],
                    })),
            };
        }

        const { data } = await getBehaviouralComparisonData(orgId, params);
        const convertedChartData = convertPaceBehaviouralComparison(data);
        handleUpdateChartData(convertedChartData, showAverage);
    };

    // People and Teams
    const handleSelectPerson = (data: TPeopleData, avoidFetching = false) => {
        setPeople(data);
        handleFetchChart<TPeopleData["selected"]>(data.selected, "individuals", avoidFetching);
    };

    const handleSelectTeam = (data: TTeamsData, avoidFetching = false) => {
        setTeams(data);
        handleFetchChart<TTeamsData["selected"]>(data.selected, "teams", avoidFetching);
    };

    const handleSetResults = <T extends BCIndividualsRefResponse | BCTeamsRefResponse>(data: T, type: TList) => {
        const { results, meta } = data;

        if (type === "individuals") {
            const convertedIndividualsRef = convertIndividualsRef(
                results as BCIndividualsRefResponse["results"],
                people.selected
            );

            setPeople({
                ...people,
                status: "done",
                data: convertedIndividualsRef,
                meta,
            });
        }

        if (type === "teams") {
            const convertedTeamsRef = convertTeamsRef(results as BCTeamsRefResponse["results"], teams.selected);

            setTeams({
                ...teams,
                status: "done",
                data: convertedTeamsRef,
                meta,
            });

            setMultiFilterData((prevVal) => ({
                ...prevVal,
                teams: convertedTeamsRef,
            }));
        }
    };

    const handlePageChange = async (ev: React.ChangeEvent<unknown>, newPage: number, type: TList) => {
        if (!orgId) return;

        if (type === "individuals") {
            setPeople({ ...people, status: "fetching" });
            const { data } = await getPaceDashboardIndividualsRef({
                ...defaultMetaParams,
                filter_pace_only: true,
                page_no: newPage,
                orgId,
            });
            handleSetResults<BCIndividualsRefResponse>(data, "individuals");
        }

        if (type === "teams") {
            const { data } = await getPaceDashboardTeamsRef({ ...defaultMetaParams, page_no: newPage, orgId });
            handleSetResults<BCTeamsRefResponse>(data, "teams");
        }
    };

    useEffect(() => {
        const selectedItems = {
            selectedTeams: multiFilterData.teams.filter((team) => team.checked).map((team) => team.name),
            selectedFunctions: multiFilterData.functions.filter((func) => func.checked).map((func) => func.name),
            selectedHierarchies: multiFilterData.hierarchies.filter((hier) => hier.checked).map((hier) => hier.name),
        };
        setSlelectedMultiFilter(selectedItems);
    }, [multiFilterData]);

    // Init
    useEffect(() => {
        // Fetch chart data
        const initialFetch = async () => {
            if (!orgId) return;

            const promises = [];

            if (!people.data?.length) {
                setPeople({ ...people, status: "fetching" });
                promises.push(getPaceDashboardIndividualsRef({ ...defaultMetaParams, filter_pace_only: true, orgId }));
            }

            if (!teams.data?.length) {
                setPeople({ ...people, status: "fetching" });
                promises.push(getPaceDashboardTeamsRef({ ...defaultMetaParams, page_size: 50, orgId }));
            }

            if (isEmpty(chartData)) {
                promises.push(getBehaviouralComparisonData(orgId, chartRequestDefaultParams));
            }

            if (promises.length) {
                try {
                    dispatch(toggleLoader(true));

                    const response = await Promise.allSettled(promises);

                    for (const result of response) {
                        if (result.status === "rejected") {
                            return {
                                error: true,
                                reason: result.reason,
                            };
                        }

                        if (result.status === "fulfilled") {
                            if (result.value.config.url?.includes("individuals")) {
                                const data = result.value.data as BCIndividualsRefResponse;
                                handleSetResults<BCIndividualsRefResponse>(data, "individuals");
                            }

                            if (result.value.config.url?.includes("teams")) {
                                const data = result.value.data as BCTeamsRefResponse;
                                handleSetResults<BCTeamsRefResponse>(data, "teams");
                            }

                            if (result.value.config.url?.includes("behavioural_comparison")) {
                                const data = result.value.data as BehaviouralComparisonResponse;
                                const convertedChartData = convertPaceBehaviouralComparison(data);
                                handleUpdateChartData(convertedChartData, showAverage);
                            }
                        }
                    }
                } catch (err) {
                    console.error(err);
                } finally {
                    dispatch(toggleLoader(false));
                }
            }
        };

        initialFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        people,
        teams,
        functions,
        hierarchies,
        multiFilterData,
        chartData,
        savedFilters,
        showAverage,
        openTeamsModal,
        openBenchmarkPopover,
        setPeople,
        setTeams,
        setFunctions,
        setHierarchies,
        setMultiFilterData,
        handleToggleAverage,
        handleOnClear,
        handleOnSave,
        handleSelectPerson,
        handleSelectTeam,
        handlePageChange,
        handleOpenBenchmarkPopover,
        handleOpenTeamsModal,
        handleCloseTeamsModal,
        handleSetResults,
    };
};

export default useBehaviouralComparison;
